import React, { useEffect, useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import { set, lensPath } from 'ramda';
import { compact, titleize, formatVertical } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { usePLPParams } from '../../hooks/usePLPParams';
import { ModuleSection } from '../../components/ModuleSection';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { SubcategoryLinks } from '../../components/SubcategoryLinks/SubcategoryLinks';
import { DesignerPageQuery, DesignerPageQueryVariables } from '../../generated/types';
import { findSectionModules } from '../../lib/findSectionModules';
import { NotFoundPage } from '../NotFoundPage';
import { LazyLoad, EnableLazyLoad } from '../../components/LazyLoad';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { DesignerPageFollowButton } from '../../components/DesignerPageFollowButton';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { useRuleVariables } from '../../hooks/useRuleVariables';
import { useShowSaleOnPLP } from '../../hooks/useShowSaleOnPLP';
import { tc } from '../../lib/trackingContext';

import './DesignerPage.scss';

export const DESIGNER_PAGE_QUERY: TypedDocumentNode<DesignerPageQuery, DesignerPageQueryVariables> =
  gql`
    query DesignerPageQuery(
      $after: String!
      $attributes: [String]
      $availability: [AvailabilityInput]
      $categoryPath: String
      $countryCode: String
      $designers: [String]
      $dynamicReRanking: Boolean
      $filterColors: [String]
      $materials: [String]
      $noSale: Boolean
      $onSale: Boolean
      $priceRange: PriceRange
      $ruleContexts: [String]
      $rules: [Rule]
      $saleTags: [String]
      $scoring: String
      $sizes: [String]
      $slug: String!
      $versionId: Int
    ) {
      displayPage(slug: $slug, version_id: $versionId) {
        slug
        name
        targetBuckets {
          name
          modules {
            ...PumoModuleFragment
          }
        }
        meta_description
      }

      designer(slug: $slug) {
        id
        name
        slug
        description
      }

      designerCollection: designer_collection(
        slug: $slug
        input: {
          attributes: $attributes
          availability: $availability
          category_path: $categoryPath
          country_code: $countryCode
          designers: $designers
          dynamic_reranking: $dynamicReRanking
          filter_colors: $filterColors
          materials: $materials
          no_sale: $noSale
          on_sale: $onSale
          price_range: $priceRange
          rule_contexts: $ruleContexts
          rules: $rules
          sale_tags: $saleTags
          scoring: $scoring
          sizes: $sizes
          version_id: $versionId
        }
      ) {
        pageName: page_name
        ...ProductListPageResultFragment
        ...PageFilterFragment
      }
    }
    ${PUMO_MODULE_FRAGMENT}
    ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
    ${PAGE_FILTER_FRAGMENT}
  `;

export const DesignerPage: React.FC = () => {
  const showSaleInPLPs = useShowSaleOnPLP();
  const {
    params: { vertical, category, subcategory, designerSlug },
    queryVariables
  } = usePLPParams();
  const ruleVariables = useRuleVariables(FacadePageType.Designer, queryVariables.slug);
  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(DESIGNER_PAGE_QUERY, {
    variables: {
      ...queryVariables,
      noSale: !showSaleInPLPs,
      ...ruleVariables
    }
  });

  const data = currentData ?? previousData;

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const firstAttribute = queryVariables.attributes?.[0];
  const designerName = data?.designerCollection?.pageName || data?.designer?.name || '';
  const categoryTree = data?.designerCollection?.categoryTree;
  const categoryText = firstAttribute || subcategory || category;
  const dataWithoutDesigners = data && set(lensPath(['designerCollection', 'designers']), [], data);

  const title =
    vertical === 'beauty'
      ? designerName
      : compact([
          designerName,
          categoryText || 'Fashion Collections',
          vertical && `for ${titleize(vertical)}`
        ]).join(' ');

  const headerText =
    vertical === 'beauty'
      ? designerName
      : compact([
          designerName,
          categoryText && titleize(categoryText),
          vertical && `for ${titleize(vertical)}`
        ]).join(' ');

  const description = compact([
    'Shop new',
    vertical && formatVertical(vertical),
    designerName,
    categoryText || 'collection',
    'on Moda Operandi.'
  ]).join(' ');

  useEffect(() => {
    tc.setCurrent('designer_name', designerName);
    return () => {
      tc.setCurrent('designer_name', undefined);
    };
  });

  const hasImageSlides = headerModules?.modules.some(
    module =>
      module.__typename === 'SinglePumoModule' &&
      module.slides?.some(slide => slide.featureImage || slide.mobileImage)
  );

  if (vertical === 'men') {
    return <Redirect to={hrefFor.HomePage()} />;
  }

  // if there are no results for the vertical, redirect to the first available vertical
  if (
    vertical &&
    categoryTree?.children?.length &&
    !categoryTree.children.find(child => child?.slug === vertical)
  )
    return (
      <Redirect
        to={hrefFor.DesignerPage({
          designerSlug: designerSlug as string,
          vertical: categoryTree?.children[0]?.slug as string
        })}
      />
    );

  if (data && !data.designerCollection) return <NotFoundPage />;

  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}

      <ProductListing
        title={title}
        description={description}
        headerText={!headerModules || !hasImageSlides ? headerText : ''}
        queryResult={dataWithoutDesigners?.designerCollection}
        error={error}
        loading={loading}
        belowHeader={
          <>
            {headerModules && <ModuleSection modules={headerModules.modules} />}
            {data?.designer?.description && (
              <div className="DesignerPage__description">{data?.designer?.description}</div>
            )}
            <DesignerPageFollowButton />
          </>
        }
      >
        <SubcategoryLinks
          designerName={designerName}
          categoryTree={dataWithoutDesigners?.designerCollection?.categoryTree || null}
        />
      </ProductListing>

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};
